import React from "react";
import { css } from "@emotion/react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import { VETTING_STEPS } from "constants/index";
import { colors, fontSize, margins } from "style";
import DisplayBox from "components/DisplayBox";
import Icon, { ICON_TYPE, ICON_SIZE } from "components/Icon";

const STEP_ICONS = {
  PROFILE_COMPLETION: ICON_TYPE.user,
  REQUIREMENTS: ICON_TYPE.file,
  QUESTIONNAIRE: ICON_TYPE.notes,
  CODE_CHALLENGE: ICON_TYPE.code,
  VIDEO_INTERVIEW: ICON_TYPE.video,
};

const STEP_ORDER = [
  VETTING_STEPS.profileCompletion,
  VETTING_STEPS.requirements,
  VETTING_STEPS.questionnaire,
  VETTING_STEPS.codeChallenge,
  VETTING_STEPS.videoInterview,
];

/**
 * ApplicationStepRequirements
 *
 * @param {Array}     steps
 * @param {Function}  onChange
 * @param {Boolean}   disabled
 * @param {Boolean}   isReadOnly
 */
const ApplicationStepRequirements = ({ steps = [], onChange, disabled = false, isReadOnly = false }) => {
  const sortedSteps = [...steps]
    .filter((step) => STEP_ORDER.includes(step.name))
    .sort((a, b) => STEP_ORDER.indexOf(a.name) - STEP_ORDER.indexOf(b.name));

  const renderStepRow = (step) => {
    const iconType = STEP_ICONS[step.name] || ICON_TYPE.file;
    const iconColor = step.required ? colors.red : colors.green;

    return (
      <div key={step.name} css={styles.step}>
        <div css={styles.leftContainer}>
          <div css={styles.iconCircle(step.required)}>
            <Icon type={iconType} size={ICON_SIZE.medium} color={iconColor} />
          </div>

          <div css={styles.label}>
            <FormattedMessage id={`Missions.Roles.RoleApplicationSteps.StepLabel.${step.name}`} />
          </div>
        </div>

        {isReadOnly ? (
          <div css={styles.readOnlyBadge(step.required)}>
            <Icon
              type={step.required ? ICON_TYPE.lock : ICON_TYPE.unlocked}
              size={ICON_SIZE.medium}
              color={step.required ? colors.darkRed : colors.darkGreen}
            />
            <span>
              {step.required ? (
                <FormattedMessage id="Missions.Roles.RoleApplicationSteps.Required" />
              ) : (
                <FormattedMessage id="Missions.Roles.RoleApplicationSteps.Optional" />
              )}
            </span>
          </div>
        ) : (
          <div css={styles.options}>
            <button
              type="button"
              disabled={disabled}
              aria-pressed={step.required}
              aria-label="Required"
              css={styles.option(step.required, "required")}
              onClick={() => onChange && onChange(step.name, true)}
            >
              <Icon
                type={ICON_TYPE.lock}
                size={ICON_SIZE.medium}
                color={step.required ? colors.darkRed : colors.grayAnatomyLight1}
              />
            </button>

            <button
              type="button"
              disabled={disabled}
              aria-pressed={!step.required}
              aria-label="Optional"
              css={styles.option(!step.required, "optional")}
              onClick={() => onChange && onChange(step.name, false)}
            >
              <Icon
                type={ICON_TYPE.unlocked}
                size={ICON_SIZE.medium}
                color={!step.required ? colors.darkGreen : colors.grayAnatomyLight1}
              />
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <DisplayBox
      title={<FormattedMessage id="Missions.Roles.RoleApplicationSteps.Title" />}
      data={sortedSteps}
      itemRenderer={renderStepRow}
      numColumns={1}
      rowGap="0rem"
      marginSize={margins.none}
    />
  );
};

const styles = {
  step: css`
    padding: 1rem 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1.5rem;
    border-bottom: 1px solid ${colors.grayAnatomyLight4};

    &:last-child {
      border-bottom: none;
    }
  `,
  leftContainer: css`
    display: flex;
    align-items: center;
    gap: 1.2rem;
    flex: 1;
    min-width: 0;
  `,
  iconCircle: (isRequired) => css`
    width: 3.6rem;
    height: 3.6rem;
    border-radius: 50%;
    background-color: ${isRequired ? colors.lightRed : colors.green6};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    i {
      display: inline-flex !important;
      align-items: center;
      justify-content: center;
    }
  `,
  label: css`
    color: ${colors.purpleRainDark2};
    font-size: ${fontSize.small};
    font-weight: 600;
  `,
  readOnlyBadge: (isRequired) => css`
    display: inline-flex;
    align-items: center;
    gap: 0.6rem;
    padding: 0.4rem 1rem;
    border-radius: 0.6rem;
    background-color: ${isRequired ? colors.lightRed : colors.green6};
    color: ${isRequired ? colors.darkRed : colors.darkGreen};
    font-size: ${fontSize.small};
    font-weight: 600;
    flex-shrink: 0;

    i {
      display: inline-flex !important;
      align-items: center;
      justify-content: center;
    }
  `,
  options: css`
    display: flex;
    flex-shrink: 0;
    background: ${colors.grayAnatomyLight5};
    border: 1px solid ${colors.grayAnatomyLight3};
    border-radius: 0.6rem;
    padding: 0.2rem;
    gap: 0.2rem;
  `,
  option: (selected, type) => css`
    width: 3.2rem;
    height: 3.2rem;
    padding: 0;
    border: 0;
    border-radius: 0.4rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: ${selected ? (type === "required" ? colors.lightRed : colors.green6) : "transparent"};
    box-shadow: ${selected ? "0 1px 2px rgba(0, 0, 0, 0.08)" : "none"};
    cursor: pointer;
    transition: all 0.15s ease-in-out;

    i {
      display: inline-flex !important;
      align-items: center;
      justify-content: center;
    }

    &:hover:not(:disabled) {
      background: ${selected ? (type === "required" ? colors.lightRed : colors.green6) : colors.white};
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }
  `,
};

ApplicationStepRequirements.propTypes = {
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      required: PropTypes.bool.isRequired,
    })
  ).isRequired,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  isReadOnly: PropTypes.bool,
};

export default ApplicationStepRequirements;
